/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { css } from '@emotion/react';
import * as R from 'ramda';
import { Column, Row } from '../layout/flexbox';
import { IAppState } from '../core/redux/IAppState';
import { getError, getLoading } from '../core/redux/api/module';
import { ACTION_TYPE } from '../core/redux/actionType';
import { loadTrainingDocumentsAction } from './loadTrainingDocumentsAction';
import { clearTrainingDocumentsAction } from './clearTrainingDocumentsAction';
import { uploadTrainingDocumentAction } from './uploadTrainingDocumentAction';
import { replaceTrainingDocumentAction } from './replaceTrainingDocumentAction';
import { deleteTrainingDocumentAction } from './deleteTrainingDocumentAction';
import { TrainingDocument } from './trainingDocument';
import TrainingDocumentFormModal from './TrainingDocumentFormModal';
import ConfirmationModal from '../forms/elements/ConfirmationModal';
import * as Buttons from '../forms/elements/buttons';
import { LargeText, PAGE_COLUMN, PAGE_CONTENT, PAGE_TITLE } from '../styles/styles';
import { Loader } from '../forms/elements/loader/Loader';
import ErrorView from '../forms/ErrorView';
import { isNilOrEmpty } from '../language/module';
import { IKON_BLUE } from '../styles/colors';
import { getResource } from '../language/resources/getResource';
import { HELP_AND_TRAINING_ROUTE } from '../core/routes';


const KEY_ACTION = ACTION_TYPE.LOAD_TRAINING_DOCS_REQUEST;

const noDocsMessage = css`
 display: block;
 margin: 25px auto;
 text-align: center;
 color: ${IKON_BLUE};
 ${LargeText};
`;

const loaderAlignment = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 25px 0;
`;

const docsLoader = css`
  width: 100px;
  height: 100px;
  margin-bottom: 20px;
`;

const loadingMsg = css`
  display: block;
  ${LargeText};
`;

const documentLink = css`
  display: block;
  padding: 12px 0;
  color: ${IKON_BLUE};
  text-decoration: none;
  ${LargeText};

  &:hover {
    text-decoration: underline;
  }
`;

const uploadButtonRow = css`
  justify-content: flex-start;
  margin-bottom: 10px;
`;

const documentRow = css`
  align-items: center;
`;

const documentRowLink = css`
  flex-grow: 1;
`;

const rowButton = css`
  margin-left: 10px;
`;


/**
 * HelpAndTrainingPage: lists the available training documents for all authenticated users. Each entry links to the
 * in-app viewer at HELP_AND_TRAINING_ROUTE/:documentId. Users whose role is in the manageHelpDocs config array also
 * get upload/replace/delete controls; the API re-checks the role on every write.
 */
export default function HelpAndTrainingPage() {
  const dispatch = useDispatch();
  const documents = useSelector((state: IAppState) => state.trainingDocuments);
  const isLoading = useSelector((state: IAppState) => !!getLoading(state, KEY_ACTION, KEY_ACTION));
  const error = useSelector((state: IAppState) => getError(state, KEY_ACTION, KEY_ACTION));
  const resources = useSelector((state: IAppState) => state.locale);
  const userRole = useSelector((state: IAppState) => state.userState?.role ?? 'agent');
  const config = useSelector((state: IAppState) => state.resortToolsConfig);
  const hasWriteError = useSelector((state: IAppState) =>
    !!getError(state, ACTION_TYPE.UPLOAD_TRAINING_DOC_REQUEST, ACTION_TYPE.UPLOAD_TRAINING_DOC_REQUEST)
    || (state.trainingDocuments ?? []).some(doc =>
      !!getError(state, String(doc.id), ACTION_TYPE.REPLACE_TRAINING_DOC_REQUEST)
      || !!getError(state, String(doc.id), ACTION_TYPE.DELETE_TRAINING_DOC_REQUEST)));

  const isAdmin = !!config && R.includes(userRole, config.manageHelpDocs);

  // 'new' opens the form empty for an upload; a document opens it prefilled for a replace.
  const [formTarget, setFormTarget] = useState<'new' | TrainingDocument | undefined>(undefined);
  const [docForDeletion, setDocForDeletion] = useState<TrainingDocument | undefined>(undefined);

  useEffect(() => {
    dispatch(loadTrainingDocumentsAction());
    return () => { dispatch(clearTrainingDocumentsAction()); };
  }, [dispatch]);

  const onFormSubmit = (title: string, sortOrder: number | undefined, file: File | undefined) => {
    if (formTarget === 'new') {
      dispatch(uploadTrainingDocumentAction(file!, title, sortOrder));
    }
    else if (formTarget) {
      dispatch(replaceTrainingDocumentAction(formTarget.id, title, sortOrder, file));
    }
    setFormTarget(undefined);
  };

  const onConfirmDelete = () => {
    if (docForDeletion) {
      dispatch(deleteTrainingDocumentAction(docForDeletion.id));
    }
    setDocForDeletion(undefined);
  };

  return (
    <Column rootCss={PAGE_COLUMN}>
      <span css={PAGE_TITLE}>{getResource(resources, 'HELP_TRAINING_PAGE_TITLE')}</span>
      <div css={PAGE_CONTENT}>
        { // Upload (admin only)
          isAdmin
          && <Row rootCss={uploadButtonRow}>
            <Buttons.BigNeutral type="button" onClick={() => setFormTarget('new')}>
              {getResource(resources, 'HELP_TRAINING_UPLOAD_BUTTON')}
            </Buttons.BigNeutral>
          </Row>
        }
        { // Loading
          isLoading
          && <div css={loaderAlignment}>
            <Loader rootCss={docsLoader}/>
            <span css={loadingMsg}>{getResource(resources, 'HELP_TRAINING_LOADING')}</span>
          </div>
        }
        {/* Errors */}
        <ErrorView error={isLoading
                          ? undefined
                          : error && getResource(resources, 'HELP_TRAINING_FETCH_FAILED')}/>
        <ErrorView error={hasWriteError ? getResource(resources, 'HELP_TRAINING_WRITE_FAILED') : undefined}/>
        { // Empty (only once loaded — undefined means the fetch hasn't resolved yet)
          documents != null
          && documents.length === 0
          && !isLoading
          && !error
          && <span css={noDocsMessage}>{getResource(resources, 'HELP_TRAINING_NO_DOCS')}</span>
        }
        { // Documents (Data)
          !isNilOrEmpty(documents) && !error && documents!.map(
            doc => <Row key={doc.id} rootCss={documentRow}>
              <Link css={[documentLink, documentRowLink]} to={`${HELP_AND_TRAINING_ROUTE}/${doc.id}`}>{doc.title}</Link>
              {
                isAdmin
                && <>
                  <Buttons.SmallSecondary type="button" css={rowButton} onClick={() => setFormTarget(doc)}>
                    {getResource(resources, 'HELP_TRAINING_REPLACE_BUTTON')}
                  </Buttons.SmallSecondary>
                  <Buttons.SmallSecondary type="button" css={rowButton} onClick={() => setDocForDeletion(doc)}>
                    {getResource(resources, 'HELP_TRAINING_DELETE_BUTTON')}
                  </Buttons.SmallSecondary>
                </>
              }
            </Row>,
          )
        }
      </div>
      {
        formTarget
        && <TrainingDocumentFormModal
          resources={resources}
          documentToReplace={formTarget === 'new' ? undefined : formTarget}
          onSubmit={onFormSubmit}
          onDismiss={() => setFormTarget(undefined)}
        />
      }
      {
        docForDeletion
        && <ConfirmationModal
          resources={resources}
          title={getResource(resources, 'HELP_TRAINING_DELETE_CONFIRM_TITLE')}
          message={getResource(resources, 'HELP_TRAINING_DELETE_CONFIRM_MSG', docForDeletion.title)}
          positiveButtonText={getResource(resources, 'HELP_TRAINING_DELETE_BUTTON')}
          negativeButtonText={getResource(resources, 'HELP_TRAINING_FORM_CANCEL')}
          onPositiveButtonClick={onConfirmDelete}
          onNegativeButtonClick={() => setDocForDeletion(undefined)}
          onDismiss={() => setDocForDeletion(undefined)}
        />
      }
    </Column>
  );
}
